import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useEditMode,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import parkingMapSvg from '../assets/parking-map.svg';

/**
 * Village parking map: the live site's retail-list + site-plan graphic
 * (https://www.woodincreekvillage.com/parking-map). Five color-coded
 * building groups sit above the plan; hovering (desktop) or tapping
 * (mobile) reveals the tenant codes that appear on the map.
 */

const mapImageDef: SlotGroupDefinition = {
  label: 'Parking Map Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Woodin Creek Village parking and retail map',
    },
  },
};

const wineAlley1: SlotGroupDefinition = { label: 'Wine Alley — Ambassador Wines', fields: { code: { label: 'Code', type: 'text', defaultValue: 'AW' }, name: { label: 'Name', type: 'text', defaultValue: 'Ambassador Wines' } } };
const wineAlley2: SlotGroupDefinition = { label: 'Wine Alley — Rooted Souls', fields: { code: { label: 'Code', type: 'text', defaultValue: 'RS' }, name: { label: 'Name', type: 'text', defaultValue: 'Rooted Souls' } } };
const wineAlley3: SlotGroupDefinition = { label: 'Wine Alley — Baer Winery', fields: { code: { label: 'Code', type: 'text', defaultValue: 'BW' }, name: { label: 'Name', type: 'text', defaultValue: 'Baer Winery' } } };
const wineAlley4: SlotGroupDefinition = { label: 'Wine Alley — Woodinville Wine Country', fields: { code: { label: 'Code', type: 'text', defaultValue: 'WWC' }, name: { label: 'Name', type: 'text', defaultValue: 'Woodinville Wine Ctry' } } };
const wineAlley5: SlotGroupDefinition = { label: 'Wine Alley — Ancestry Cellars', fields: { code: { label: 'Code', type: 'text', defaultValue: 'AC' }, name: { label: 'Name', type: 'text', defaultValue: 'Ancestry Cellars' } } };
const wineAlley6: SlotGroupDefinition = { label: 'Wine Alley — Adams Bench', fields: { code: { label: 'Code', type: 'text', defaultValue: 'AB' }, name: { label: 'Name', type: 'text', defaultValue: 'Adams Bench' } } };
const wineAlley7: SlotGroupDefinition = { label: 'Wine Alley — Truth Teller', fields: { code: { label: 'Code', type: 'text', defaultValue: 'T' }, name: { label: 'Name', type: 'text', defaultValue: 'Truth Teller' } } };
const wineAlley8: SlotGroupDefinition = { label: 'Wine Alley — Rusty Pelican', fields: { code: { label: 'Code', type: 'text', defaultValue: 'RP' }, name: { label: 'Name', type: 'text', defaultValue: 'Rusty Pelican' } } };

const elevations1: SlotGroupDefinition = { label: 'Elevations — Sidekick Coffee', fields: { code: { label: 'Code', type: 'text', defaultValue: 'SC' }, name: { label: 'Name', type: 'text', defaultValue: 'Sidekick Coffee' } } };
const elevations2: SlotGroupDefinition = { label: 'Elevations — WCV Leasing Office', fields: { code: { label: 'Code', type: 'text', defaultValue: 'WCV' }, name: { label: 'Name', type: 'text', defaultValue: 'WCV Leasing Office' } } };
const elevations3: SlotGroupDefinition = { label: 'Elevations — Ma‘ono Fried Chicken', fields: { code: { label: 'Code', type: 'text', defaultValue: 'MFC' }, name: { label: 'Name', type: 'text', defaultValue: 'Ma‘ono Fried Chicken' } } };
const elevations4: SlotGroupDefinition = { label: 'Elevations — Fresh Cuts', fields: { code: { label: 'Code', type: 'text', defaultValue: 'FC' }, name: { label: 'Name', type: 'text', defaultValue: 'Fresh Cuts' } } };
const elevations5: SlotGroupDefinition = { label: 'Elevations — Dashing Nails', fields: { code: { label: 'Code', type: 'text', defaultValue: 'DN' }, name: { label: 'Name', type: 'text', defaultValue: 'Dashing Nails' } } };
const reserve1: SlotGroupDefinition = { label: 'The Reserve — Woodin Creek Kitchen & Tap', fields: { code: { label: 'Code', type: 'text', defaultValue: 'W' }, name: { label: 'Name', type: 'text', defaultValue: 'Woodcreek Kitchen & Tap' } } };
const reserve2: SlotGroupDefinition = { label: 'The Reserve — Swanky Scoop', fields: { code: { label: 'Code', type: 'text', defaultValue: 'SS' }, name: { label: 'Name', type: 'text', defaultValue: 'Swanky Scoop' } } };
const reserve3: SlotGroupDefinition = { label: 'The Reserve — Island Blends', fields: { code: { label: 'Code', type: 'text', defaultValue: 'IB' }, name: { label: 'Name', type: 'text', defaultValue: 'Island Blends' } } };
const reserve4: SlotGroupDefinition = { label: 'The Reserve — Tsillan Cellars', fields: { code: { label: 'Code', type: 'text', defaultValue: 'TC' }, name: { label: 'Name', type: 'text', defaultValue: 'Tsillan Cellars' } } };
const reserve5: SlotGroupDefinition = { label: 'The Reserve — Brix', fields: { code: { label: 'Code', type: 'text', defaultValue: 'B' }, name: { label: 'Name', type: 'text', defaultValue: 'Brix' } } };

const summit1: SlotGroupDefinition = { label: 'Summit — Alexandria Nicole Cellars', fields: { code: { label: 'Code', type: 'text', defaultValue: 'ANC' }, name: { label: 'Name', type: 'text', defaultValue: 'Alexandria Nichole Cellars' } } };
const summit2: SlotGroupDefinition = { label: 'Summit — Brian Carter Cellars', fields: { code: { label: 'Code', type: 'text', defaultValue: 'BCC' }, name: { label: 'Name', type: 'text', defaultValue: 'Brian Carter Cellars' } } };
const summit3: SlotGroupDefinition = { label: 'Summit — Bayernmoor Cellars', fields: { code: { label: 'Code', type: 'text', defaultValue: 'BC' }, name: { label: 'Name', type: 'text', defaultValue: 'Bayernmoor Cellars' } } };

type ZoneId = 'wine-alley' | 'elevations' | 'reserve' | 'summit';

const zones: {
  id: ZoneId;
  titlePath: string;
  titleFallback: string;
  places: { path: string; definition: SlotGroupDefinition }[];
}[] = [
  {
    id: 'wine-alley',
    titlePath: 'parkingmap.zone_wine_alley',
    titleFallback: 'Wine Alley',
    places: [
      { path: 'parkingmap.winealley1', definition: wineAlley1 },
      { path: 'parkingmap.winealley2', definition: wineAlley2 },
      { path: 'parkingmap.winealley3', definition: wineAlley3 },
      { path: 'parkingmap.winealley4', definition: wineAlley4 },
      { path: 'parkingmap.winealley5', definition: wineAlley5 },
      { path: 'parkingmap.winealley6', definition: wineAlley6 },
      { path: 'parkingmap.winealley7', definition: wineAlley7 },
      { path: 'parkingmap.winealley8', definition: wineAlley8 },
    ],
  },
  {
    id: 'elevations',
    titlePath: 'parkingmap.zone_elevations',
    titleFallback: 'Elevations',
    places: [
      { path: 'parkingmap.elevations1', definition: elevations1 },
      { path: 'parkingmap.elevations2', definition: elevations2 },
      { path: 'parkingmap.elevations3', definition: elevations3 },
      { path: 'parkingmap.elevations4', definition: elevations4 },
      { path: 'parkingmap.elevations5', definition: elevations5 },
    ],
  },
  {
    id: 'reserve',
    titlePath: 'parkingmap.zone_reserve',
    titleFallback: 'The Reserve',
    places: [
      { path: 'parkingmap.reserve1', definition: reserve1 },
      { path: 'parkingmap.reserve2', definition: reserve2 },
      { path: 'parkingmap.reserve3', definition: reserve3 },
      { path: 'parkingmap.reserve4', definition: reserve4 },
      { path: 'parkingmap.reserve5', definition: reserve5 },
    ],
  },
  {
    id: 'summit',
    titlePath: 'parkingmap.zone_summit',
    titleFallback: 'Summit',
    places: [
      { path: 'parkingmap.summit1', definition: summit1 },
      { path: 'parkingmap.summit2', definition: summit2 },
      { path: 'parkingmap.summit3', definition: summit3 },
    ],
  },
];

function TenantRow({
  path,
  definition,
}: {
  path: string;
  definition: SlotGroupDefinition;
}) {
  const place = useSlotGroup(path, definition);
  const editMode = useEditMode();
  if (!place.name && !place.code && !editMode?.isEditMode) return null;

  return (
    <EditableSlotGroup groupPath={path} definition={definition} className="parking-map__tenant">
      <span className="parking-map__code">{place.code}</span>
      <span className="parking-map__name">{place.name}</span>
    </EditableSlotGroup>
  );
}

function ZoneColumn({
  zone,
  open,
  onToggle,
  forceOpen,
}: {
  zone: (typeof zones)[number];
  open: boolean;
  onToggle: () => void;
  forceOpen: boolean;
}) {
  const title = useContentToken(zone.titlePath);
  const showList = forceOpen || open;

  return (
    <div className={cn('parking-map__zone', `parking-map__zone--${zone.id}`, showList && 'is-open')}>
      <button type="button" className="parking-map__zone-title" aria-expanded={showList} onClick={onToggle}>
        <EditableToken path={zone.titlePath} as="span">
          {title || zone.titleFallback}
        </EditableToken>
        <svg className="parking-map__chevron" viewBox="0 0 448 512" aria-hidden="true">
          <path
            fill="currentColor"
            d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z"
          />
        </svg>
      </button>
      <ul className={cn('parking-map__tenants', !showList && 'is-collapsed')}>
        {zone.places.map((place) => (
          <li key={place.path}>
            <TenantRow path={place.path} definition={place.definition} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ParkingMap() {
  const [openZone, setOpenZone] = useState<ZoneId | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const editMode = useEditMode();
  const forceOpen = !!editMode?.isEditMode;
  const retailLabel = useContentToken('parkingmap.retail_list');
  const map = useSlotGroup('parkingmap.map', mapImageDef);
  const mapSrc = map.image || parkingMapSvg;
  const mapAlt = map.alt || 'Woodin Creek Village parking and retail map';

  return (
    <section className="parking-map bg-background">
      <div className="parking-map__legend">
        <button
          type="button"
          className="parking-map__retail-toggle"
          aria-expanded={listOpen}
          onClick={() => setListOpen((current) => !current)}
        >
          <EditableToken path="parkingmap.retail_list" as="span">
            {retailLabel || 'Retail List'}
          </EditableToken>
        </button>

        <div className={cn('parking-map__zones', listOpen && 'is-open')}>
          {zones.map((zone) => (
            <ZoneColumn
              key={zone.id}
              zone={zone}
              open={openZone === zone.id}
              forceOpen={forceOpen}
              onToggle={() => setOpenZone((current) => (current === zone.id ? null : zone.id))}
            />
          ))}
        </div>
      </div>

      <EditableSlotGroup groupPath="parkingmap.map" definition={mapImageDef} className="parking-map__canvas">
        {map.image ? (
          <VibesitesImage url={map.image} alt={mapAlt} sizes="100vw" className="parking-map__image" />
        ) : (
          <img src={mapSrc} alt={mapAlt} className="parking-map__image" />
        )}
      </EditableSlotGroup>

      <EditableToken path="parkingmap.zone_wine_alley" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="parkingmap.zone_elevations" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="parkingmap.zone_reserve" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="parkingmap.zone_summit" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableSlotGroup groupPath="parkingmap.winealley1" definition={wineAlley1} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley2" definition={wineAlley2} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley3" definition={wineAlley3} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley4" definition={wineAlley4} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley5" definition={wineAlley5} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley6" definition={wineAlley6} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley7" definition={wineAlley7} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.winealley8" definition={wineAlley8} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.elevations1" definition={elevations1} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.elevations2" definition={elevations2} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.elevations3" definition={elevations3} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.elevations4" definition={elevations4} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.elevations5" definition={elevations5} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.reserve1" definition={reserve1} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.reserve2" definition={reserve2} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.reserve3" definition={reserve3} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.reserve4" definition={reserve4} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.reserve5" definition={reserve5} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.summit1" definition={summit1} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.summit2" definition={summit2} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableSlotGroup groupPath="parkingmap.summit3" definition={summit3} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <style>{`
        .parking-map {
          --pm-bar: #c2b69c;
          --pm-wine-title: #4d220d;
          --pm-wine-list: #683115;
          --pm-elev-title: #935a20;
          --pm-elev-list: #b06b28;
          --pm-reserve-title: #404d1e;
          --pm-reserve-list: #57662d;
          --pm-summit-title: #60752e;
          --pm-summit-list: #7f9942;
          --pm-hover: #ebd751;
          --pm-code: #1a1d4b;
        }
        .parking-map__legend {
          background: var(--pm-bar);
        }
        .parking-map__retail-toggle {
          display: none;
          width: 100%;
          padding: 0.7rem 1rem;
          background: #944a21;
          color: #fff;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-align: left;
          border: 0;
        }
        .parking-map__zones {
          display: flex;
          width: 100%;
        }
        .parking-map__zone {
          position: relative;
          flex: 1 1 0;
          min-width: 0;
        }
        .parking-map__zone-title {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          padding: 0.7rem 1rem;
          border: 0;
          color: #fff;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
        }
        .parking-map__zone--wine-alley .parking-map__zone-title { background: var(--pm-wine-title); }
        .parking-map__zone--elevations .parking-map__zone-title { background: var(--pm-elev-title); }
        .parking-map__zone--reserve .parking-map__zone-title { background: var(--pm-reserve-title); }
        .parking-map__zone--summit .parking-map__zone-title { background: var(--pm-summit-title); }
        .parking-map__zone--wine-alley .parking-map__tenants { background: var(--pm-wine-list); }
        .parking-map__zone--elevations .parking-map__tenants { background: var(--pm-elev-list); }
        .parking-map__zone--reserve .parking-map__tenants { background: var(--pm-reserve-list); }
        .parking-map__zone--summit .parking-map__tenants { background: var(--pm-summit-list); }
        .parking-map__chevron {
          width: 0.65rem;
          height: 0.65rem;
          flex-shrink: 0;
        }
        .parking-map__tenants {
          list-style: none;
          margin: 0;
          padding: 0.65rem 0.85rem;
        }
        .parking-map__tenants.is-collapsed {
          display: none;
        }
        .parking-map__tenant {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.28rem 0;
          color: #fff;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
        }
        .parking-map__code {
          display: inline-flex;
          min-width: 2.5rem;
          height: 1.25rem;
          align-items: center;
          justify-content: center;
          padding: 0 0.35rem;
          border-radius: 999px;
          background: #fff;
          color: var(--pm-code);
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .parking-map__tenant:hover .parking-map__name,
        .parking-map__tenant:hover .parking-map__code {
          color: var(--pm-code);
        }
        .parking-map__tenant:hover {
          color: var(--pm-hover);
        }
        .parking-map__tenant:hover .parking-map__code {
          background: var(--pm-hover);
        }
        .parking-map,
        .parking-map__canvas {
          overflow: visible;
        }
        .parking-map__canvas {
          display: block;
          width: 100%;
          aspect-ratio: 790 / 490;
          background: #f4f1ea;
        }
        .parking-map__image,
        .parking-map__canvas img {
          display: block;
          width: 100%;
          height: 100%;
          max-width: none;
          object-fit: contain;
          object-position: center;
        }
        @media (min-width: 992px) {
          .parking-map__tenants {
            position: absolute;
            left: 0;
            right: 0;
            z-index: 2;
          }
          .parking-map__zone:hover .parking-map__tenants,
          .parking-map__zone.is-open .parking-map__tenants {
            display: block;
          }
        }
        @media (max-width: 991px) {
          .parking-map__retail-toggle {
            display: block;
          }
          .parking-map__zones {
            display: none;
            flex-direction: column;
          }
          .parking-map__zones.is-open {
            display: flex;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ParkingMap',
  displayName: 'Parking Map',
  tokens: {
    'parkingmap.retail_list': {
      label: 'Retail List Label',
      type: 'text',
      defaultValue: 'Retail List',
    },
    'parkingmap.zone_wine_alley': {
      label: 'Wine Alley Title',
      type: 'text',
      defaultValue: 'Wine Alley',
    },
    'parkingmap.zone_elevations': {
      label: 'Elevations Title',
      type: 'text',
      defaultValue: 'Elevations',
    },
    'parkingmap.zone_reserve': {
      label: 'The Reserve Title',
      type: 'text',
      defaultValue: 'The Reserve',
    },
    'parkingmap.zone_summit': {
      label: 'Summit Title',
      type: 'text',
      defaultValue: 'Summit',
    },
  },
  slotGroups: {
    'parkingmap.map': mapImageDef,
    'parkingmap.winealley1': wineAlley1,
    'parkingmap.winealley2': wineAlley2,
    'parkingmap.winealley3': wineAlley3,
    'parkingmap.winealley4': wineAlley4,
    'parkingmap.winealley5': wineAlley5,
    'parkingmap.winealley6': wineAlley6,
    'parkingmap.winealley7': wineAlley7,
    'parkingmap.winealley8': wineAlley8,
    'parkingmap.elevations1': elevations1,
    'parkingmap.elevations2': elevations2,
    'parkingmap.elevations3': elevations3,
    'parkingmap.elevations4': elevations4,
    'parkingmap.elevations5': elevations5,
    'parkingmap.reserve1': reserve1,
    'parkingmap.reserve2': reserve2,
    'parkingmap.reserve3': reserve3,
    'parkingmap.reserve4': reserve4,
    'parkingmap.reserve5': reserve5,
    'parkingmap.summit1': summit1,
    'parkingmap.summit2': summit2,
    'parkingmap.summit3': summit3,
  },
};

(ParkingMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ParkingMap;
